import * as Sentry from '@sentry/nextjs';
import Head from 'next/head';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect } from 'react';

import getConfig from '@/configs/global';
import { generateHreflangLinks } from '@/helpers/hreflang';
import { formatPriceLabel } from '@/helpers/locationPages';
import extractSeoData from '@/helpers/seo';
import strapiApi from '@/helpers/strapi/api';
import { getPricesBySlugsIndependent } from '@/helpers/strapi/helpers';
import {
  generateBreadcrumbStructuredData,
  generateFaqStructuredData,
  generateLogoStructuredData,
  generateStaticFaqStructuredData,
} from '@/helpers/strapi/structuredData';
import { useSourceContext } from '@/hooks/useSourceContext';
import DefaultLayout from '@/layouts/default';
import Page from '@/templates/Page/Page';

const {
  publicRuntimeConfig: { BASE_URL },
} = getConfig();

/**
 * It's heavily encouraged to benefit from optional chaining in this file,
 * given that data from Strapi might be provided corrupted or deficient.
 * In such scenarios, optional chaining allows us to have non-broken components.
 */
const DynamicPage = ({ page, breadcrumbs, refererPathname, pageProps }) => {
  const { asPath, query } = useRouter();
  const seoData = page?.attributes?.seo;

  const hasStrapiSFaq = page?.attributes?.body?.some(
    c => c?.__component === 'faq.frequently-asked-questions'
  );
  const hasStaticFaq =
    !hasStrapiSFaq &&
    page?.attributes?.body?.some(
      c => c?.__component === 'shared.placeholder' && c?.type === 'faq_team'
    );
  const {
    keywords,
    ogImageUrl,
    twitterTitle,
    twitterDescription,
    twitterImageUrl,
  } = extractSeoData(seoData);
  const [pathname] = asPath.split('?');
  const { setLeadReference } = useSourceContext();
  const ogUrl = `${BASE_URL}${pathname}`;

  useEffect(() => {
    setLeadReference(pathname);
  }, [pathname, setLeadReference, query.productReferenceId]);

  return (
    <>
      <Head>
        <title>{seoData?.metaTitle || page.attributes.title}</title>
        <meta
          key="description"
          name="description"
          content={seoData?.metaDescription || page.attributes.description}
        />
        {keywords && <meta name="keywords" content={keywords}></meta>}
        <meta
          property="og:title"
          content={seoData?.metaTitle || page.attributes.title}
          key="og:title"
        />
        <meta
          property="og:description"
          content={seoData?.metaDescription || page.attributes.description}
          key="og:description"
        />
        {ogImageUrl && (
          <>
            <meta property="og:image" content={ogImageUrl} key="og:image" />
            <meta
              property="og:image:secure_url"
              content={ogImageUrl}
              key="og:image:secure_url"
            />
          </>
        )}
        <meta property="og:url" content={ogUrl} key="og:url"></meta>
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:locale" content="de_DE" key="og:locale" />
        <meta
          name="twitter:card"
          content="summary_large_image"
          key="twitter:card"
        />
        {twitterTitle && (
          <meta
            name="twitter:title"
            content={twitterTitle}
            key="twitter:title"
          />
        )}
        {twitterDescription && (
          <meta
            name="twitter:description"
            content={twitterDescription}
            key="twitter:description"
          />
        )}
        {twitterImageUrl && (
          <meta
            name="twitter:image"
            content={twitterImageUrl}
            key="twitter:image"
          />
        )}
        {seoData?.structuredData && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(seoData?.structuredData),
            }}
            key="page-jsonld"
          />
        )}
        {generateBreadcrumbStructuredData(breadcrumbs)}
        {generateFaqStructuredData(page)}
        {hasStaticFaq && generateStaticFaqStructuredData()}
        {generateLogoStructuredData(pathname)}
        {pathname === '/' && generateHreflangLinks()}
      </Head>
      <Page
        page={page}
        refererPathname={refererPathname}
        pageProps={pageProps}
      />
    </>
  );
};

DynamicPage.getLayout = function getLayout(page) {
  return (
    <DefaultLayout breadcrumbs={page.props.breadcrumbs}>{page}</DefaultLayout>
  );
};

DynamicPage.propTypes = {
  page: PropTypes.object,
};

DynamicPage.defaultProps = {
  page: undefined,
};

export async function getServerSideProps({ res, req, resolvedUrl }) {
  const [pathname, queryParams] = resolvedUrl.split('?');
  const decodedPathname = decodeURI(pathname);
  const pageData = (await strapiApi.getPageByPathname(decodedPathname)).data
    .data;
  const statusCode = pageData?.id ? 200 : 404;
  const referer = req.headers?.referer;
  let refererPathname;

  let breadcrumbsData = null;

  /**
   * `res` is available only on the server-side where `statusCode` is meaningful
   */
  if (res) {
    res.statusCode = statusCode;
  }

  if (!pageData?.id) {
    return {
      props: {},
      notFound: true,
    };
  }

  if (decodedPathname !== decodedPathname.toLowerCase()) {
    const destination = [encodeURI(decodedPathname.toLowerCase()), queryParams]
      .filter(Boolean)
      .join('?');

    return {
      redirect: {
        destination,
        permanent: true,
      },
    };
  }

  if (statusCode === 200) {
    breadcrumbsData = (
      await strapiApi.getBreadcrumbsByPathname(decodedPathname)
    ).data.data;
  }

  if (referer) {
    try {
      refererPathname = new URL(referer).pathname;
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  const dametisComponents =
    pageData.attributes.body?.filter(
      c => c.__component === 'catalogs.dametis-product-catalog'
    ) ?? [];

  let dametisProductData = {};
  if (dametisComponents.length) {
    const results = await Promise.all(
      dametisComponents.map(async c => {
        const id = c.productGroup.data.attributes.blob.id;
        const { data } = await strapiApi.getProductTypesByGroup(id);
        const products = data.data?.[0]?.attributes?.product_types?.data ?? [];
        return [id, products];
      })
    );
    dametisProductData = Object.fromEntries(results);
  }

  // Enrich two-column-grid tiles with min pricing from Strapi
  const gridComponents =
    pageData.attributes.body?.filter(
      c => c.__component === 'two-column-grid.two-column-grid'
    ) ?? [];

  if (gridComponents.length) {
    const linkPattern = /^\/mieten\/([^/]+)\/([^/]+)(?:\/([^/]+))?$/;
    const targetSlugs = new Set();

    for (const grid of gridComponents) {
      for (const tile of grid.tiles ?? []) {
        const match = tile.link?.match(linkPattern);
        if (match) {
          const specificSlug = (match[3] || match[2]).replace(/_/g, '-');
          targetSlugs.add(specificSlug);
        }
      }
    }

    let priceLookup = {};

    if (targetSlugs.size > 0) {
      priceLookup = await getPricesBySlugsIndependent([...targetSlugs]);

      for (const grid of gridComponents) {
        for (const tile of grid.tiles ?? []) {
          const match = tile.link?.match(linkPattern);
          if (match) {
            const specificSlug = (match[3] || match[2]).replace(/_/g, '-');
            const minPrice = priceLookup[specificSlug];

            const label = formatPriceLabel(
              minPrice?.price,
              minPrice?.priceBasis
            );
            if (label) {
              tile.headline = `<span style="display:flex;align-items:baseline;gap:8px">${tile.headline}<span style="font-weight:400;font-size:0.875rem;color:#0065bd;margin-left:auto;white-space:nowrap">${label}</span></span>`;
            }
          }
        }
      }
    }
  }

  return {
    props: {
      page: pageData,
      breadcrumbs: breadcrumbsData,
      refererPathname: refererPathname || null,
      pageProps: { dametisProductData },
    },
  };
}

export default DynamicPage;
